/**
 * currencyLedger — atomic transaction recording for currency awards.
 *
 * When a domain event awards a currency, this service:
 * 1. Writes a transaction doc to ledger_{name}_transactions
 * 2. Upserts the student's running balance in ledger_{name}_balances
 * 3. If the currency has groupBy, increments the group total + weekly period
 *
 * All three writes happen in a single Firestore transaction for atomicity.
 */

import {
  doc,
  collection,
  runTransaction,
  serverTimestamp,
  increment,
  onSnapshot,
  getDocs,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { deriveLedgerCollections } from './currencyService';
import { resolveHouseForStudent } from '../utils/houseResolver';

/**
 * Get the current ISO week string (e.g. "2026-W11").
 */
export function getISOWeek(date = new Date()) {
  if (date?.toDate) date = date.toDate();
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) date = new Date();
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

export function buildTransactionId(txData = {}) {
  if (!txData.sourceId) return null;
  const raw = `${txData.domainId || txData.sourceType || 'event'}__${txData.sourceId}`;
  return raw.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 500);
}

/**
 * Resolve a student's group value based on the currency's groupBy field.
 * Currently supports "house" — extend later for class, grade, etc.
 *
 * @param {string} studentId
 * @param {string} schoolId
 * @param {string} groupBy — student field name (e.g. "house")
 * @returns {Promise<{groupValue: string|null, groupId: string|null}>}
 */
async function resolveGroup(studentId, schoolId, groupBy) {
  if (!groupBy || !studentId || !schoolId) {
    return { groupValue: null, groupId: null };
  }

  if (groupBy === 'house') {
    const { houseId, houseName } = await resolveHouseForStudent(studentId, schoolId);
    return { groupValue: houseName, groupId: houseId };
  }

  // Future: read student[groupBy] directly for generic fields
  return { groupValue: null, groupId: null };
}

/**
 * Record a currency transaction atomically.
 *
 * @param {string} schoolId
 * @param {Object} currencyConfig — full currency doc (id, name, groupBy, etc.)
 * @param {Object} txData — transaction details:
 *   { studentId, studentName, className, amount, sourceType, sourceId,
 *     domainId, typeLabel, issuedById, issuedByName }
 * @returns {Promise<string|null>} transaction document ID, or null when a
 * grouped currency cannot resolve the learner's group
 */
export async function recordTransaction(schoolId, currencyConfig, txData) {
  if (!schoolId) throw new Error('recordTransaction: schoolId is required');
  if (!currencyConfig?.name) throw new Error('recordTransaction: currencyConfig.name is required');
  if (!txData.studentId) throw new Error('recordTransaction: studentId is required');
  if (txData.amount == null) throw new Error('recordTransaction: amount is required');

  const cols = deriveLedgerCollections(currencyConfig.name);

  // Resolve group before entering transaction (reads not allowed inside tx on external collections)
  const { groupValue, groupId } = await resolveGroup(
    txData.studentId,
    schoolId,
    currencyConfig.groupBy
  );

  if (currencyConfig.groupBy && !groupId) {
    console.warn(
      `[currencyLedger] ${txData.sourceId || 'event'} was not credited: ` +
      `${currencyConfig.groupBy} could not be resolved for student ${txData.studentId}`
    );
    return null;
  }

  const occurredAt = txData.occurredAt || null;
  const weekKey = getISOWeek(occurredAt?.toDate ? occurredAt.toDate() : occurredAt || new Date());

  // Source-backed transactions use deterministic IDs so retries and the
  // server-side safety trigger cannot double-count the same event.
  const transactionId = buildTransactionId(txData);
  const txRef = transactionId
    ? doc(db, 'schools', schoolId, cols.transactions, transactionId)
    : doc(collection(db, 'schools', schoolId, cols.transactions));
  const balanceRef = doc(db, 'schools', schoolId, cols.balances, txData.studentId);
  const groupRef = groupId
    ? doc(db, 'schools', schoolId, cols.groups, groupId)
    : null;

  await runTransaction(db, async (transaction) => {
    const [existingTx, balanceSnap, groupSnap] = await Promise.all([
      transaction.get(txRef),
      transaction.get(balanceRef),
      groupRef ? transaction.get(groupRef) : Promise.resolve(null),
    ]);

    if (existingTx.exists()) return;

    // 1. Write transaction doc
    transaction.set(txRef, {
      studentId: txData.studentId,
      studentName: txData.studentName || null,
      className: txData.className || null,
      amount: txData.amount,
      currencyId: currencyConfig.id,
      sourceType: txData.sourceType || 'domain_event',
      sourceId: txData.sourceId || null,
      domainId: txData.domainId || null,
      typeLabel: txData.typeLabel || null,
      groupValue: groupValue || null,
      groupId: groupId || null,
      issuedById: txData.issuedById || null,
      issuedByName: txData.issuedByName || null,
      schoolId,
      occurredAt: occurredAt || serverTimestamp(),
      createdAt: serverTimestamp(),
    });

    // 2. Upsert student balance
    if (!balanceSnap.exists()) {
      transaction.set(balanceRef, {
        studentId: txData.studentId,
        studentName: txData.studentName || null,
        schoolId,
        total: txData.amount,
        groupValue: groupValue || null,
        groupId: groupId || null,
        lastUpdated: serverTimestamp(),
      });
    } else {
      transaction.update(balanceRef, {
        total: increment(txData.amount),
        lastUpdated: serverTimestamp(),
      });
    }

    // 3. Upsert group total + weekly period
    if (groupRef) {
      if (!groupSnap.exists()) {
        transaction.set(groupRef, {
          groupValue,
          groupId,
          schoolId,
          total: txData.amount,
          periods: { [weekKey]: { total: txData.amount } },
          lastUpdated: serverTimestamp(),
        });
      } else {
        transaction.update(groupRef, {
          total: increment(txData.amount),
          [`periods.${weekKey}.total`]: increment(txData.amount),
          lastUpdated: serverTimestamp(),
        });
      }
    }
  });

  return txRef.id;
}

/**
 * Get a student's balance for a currency.
 * @param {string} schoolId
 * @param {string} currencyName — machine name
 * @param {string} studentId
 * @returns {Promise<{total: number}|null>}
 */
export async function getStudentBalance(schoolId, currencyName, studentId) {
  if (!schoolId || !currencyName || !studentId) return null;

  const cols = deriveLedgerCollections(currencyName);
  const ref = doc(db, 'schools', schoolId, cols.balances, studentId);
  const { getDoc } = await import('firebase/firestore');
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * Get group standings for a currency (e.g. house leaderboard).
 * @param {string} schoolId
 * @param {string} currencyName — machine name
 * @returns {Promise<Array>} sorted by total descending
 */
export async function getGroupStandings(schoolId, currencyName) {
  if (!schoolId || !currencyName) return [];

  const cols = deriveLedgerCollections(currencyName);
  const ref = collection(db, 'schools', schoolId, cols.groups);
  const snap = await getDocs(ref);
  const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  items.sort((a, b) => (b.total || 0) - (a.total || 0));
  return items;
}

/**
 * Subscribe to group standings in real-time (for leaderboard widgets).
 * @param {string} schoolId
 * @param {string} currencyName — machine name
 * @param {Function} callback — receives sorted array
 * @returns {Function} unsubscribe
 */
export function subscribeGroupStandings(schoolId, currencyName, callback) {
  if (!schoolId || !currencyName) {
    callback([]);
    return () => {};
  }

  const cols = deriveLedgerCollections(currencyName);
  const ref = collection(db, 'schools', schoolId, cols.groups);

  return onSnapshot(ref, (snap) => {
    const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    items.sort((a, b) => (b.total || 0) - (a.total || 0));
    callback(items);
  });
}

export function aggregateTransactionTotals(transactions = []) {
  const totals = { awarded: 0, deducted: 0, net: 0, byGroup: {} };

  transactions.forEach((transaction) => {
    const amount = Number(transaction.amount) || 0;
    const groupId = transaction.groupId || null;

    totals.net += amount;
    if (amount > 0) totals.awarded += amount;
    if (amount < 0) totals.deducted += Math.abs(amount);

    if (groupId) {
      if (!totals.byGroup[groupId]) {
        totals.byGroup[groupId] = { awarded: 0, deducted: 0, net: 0 };
      }
      totals.byGroup[groupId].net += amount;
      if (amount > 0) totals.byGroup[groupId].awarded += amount;
      if (amount < 0) totals.byGroup[groupId].deducted += Math.abs(amount);
    }
  });

  return totals;
}

/**
 * Subscribe to transaction totals for a date range.
 *
 * Keeps positive awards separate from deductions so a negative net score does
 * not hide the recognitions that were awarded during the week.
 *
 * @param {string} schoolId
 * @param {string} currencyName
 * @param {Date} startDate inclusive
 * @param {Date} endDate exclusive
 * @param {Function} callback
 * @returns {Function} unsubscribe
 */
export function subscribeTransactionTotals(
  schoolId,
  currencyName,
  startDate,
  endDate,
  callback
) {
  const emptyTotals = { awarded: 0, deducted: 0, net: 0, byGroup: {} };

  if (!schoolId || !currencyName || !startDate || !endDate) {
    callback(emptyTotals);
    return () => {};
  }

  const cols = deriveLedgerCollections(currencyName);
  const ref = query(
    collection(db, 'schools', schoolId, cols.transactions),
    where('occurredAt', '>=', Timestamp.fromDate(startDate)),
    where('occurredAt', '<', Timestamp.fromDate(endDate))
  );

  return onSnapshot(ref, (snap) => {
    callback(aggregateTransactionTotals(snap.docs.map(docSnap => docSnap.data())));
  });
}
